import styled from "styled-components";

// ── Styled Components ──────────────────────────────────────────────

export const SectionStyled = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: radial-gradient(circle, var(--nd-border) 0.5px, transparent 0.5px);
    background-size: 12px 12px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

export const HeroBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  display: block;
  margin-bottom: 24px;
`;

export const HeroTitle = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 64px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  line-height: 1.0;
  margin-bottom: 24px;
  @media (max-width: 768px) { font-size: 40px; }
`;

export const HeroSubtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 560px;
  margin-bottom: 48px;
`;

export const Section = styled.section`
  padding: 96px 0;
  background: var(--nd-black);
`;

export const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 8px;
`;

export const SectionDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  max-width: 600px;
  margin-bottom: 48px;
`;

// ── Skills & Certifications ────────────────────────────────────────

export const SkillRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  border-bottom: 1px solid var(--nd-border);
  &:last-child { border-bottom: none; }
`;

export const SkillTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  @media (max-width: 576px) { flex-direction: column; align-items: flex-start; }
`;

export const SkillRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ProofToggle = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  color: var(--nd-text-secondary);
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  &:hover { color: var(--nd-text-display); border-color: var(--nd-text-display); }
`;

export const ProofPanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: var(--nd-surface-raised);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  display: ${({ open }) => (open ? "block" : "none")};
`;

export const ProofTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
`;

export const ProofTab = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? "var(--nd-text-display)" : "var(--nd-border-visible)")};
  background: ${({ active }) => (active ? "var(--nd-text-display)" : "transparent")};
  color: ${({ active }) => (active ? "var(--nd-black)" : "var(--nd-text-disabled)")};
  cursor: pointer;
  outline: none;
`;

export const ProofInput = styled.input`
  width: 100%;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--nd-border-visible);
  color: var(--nd-text-primary);
  padding: 6px 4px;
  outline: none;
  &:focus { border-color: var(--nd-text-display); }
`;

export const ProofRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const ProofSaveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

export const ProofExisting = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-interactive) !important;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

export const ProofBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-success);
  margin-left: 8px;
`;

export const SignInProofCTA = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
`;

export const SkillName = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
  min-width: 200px;
`;

export const LevelButtons = styled.div` display: flex; gap: 4px; `;

export const LevelBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border)"};
  background: ${({ active }) => active ? "var(--nd-text-display)" : "transparent"};
  color: ${({ active }) => active ? "var(--nd-black)" : "var(--nd-text-disabled)"};
  cursor: pointer;
  outline: none;
  transition: all 200ms ease;
  &:hover { border-color: var(--nd-text-secondary); }
`;

export const MentorshipCallout = styled.div`
  border-left: 3px solid var(--nd-accent);
  background: var(--nd-surface);
  padding: 16px 20px;
  margin-bottom: 24px;
  border-radius: 0 8px 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MentorshipLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-accent);
`;

export const MentorshipText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  line-height: 1.5;
  margin: 0;
`;

export const CertCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

export const CertRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const CertActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CertName = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
`;

export const CertIssuer = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-disabled);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const CertStatus = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ status }) =>
    status === "achieved" ? "rgba(74,158,92,0.4)" :
    status === "in_progress" ? "rgba(212,168,67,0.4)" :
    "var(--nd-border-visible)"};
  background: ${({ status }) =>
    status === "achieved" ? "rgba(74,158,92,0.15)" :
    status === "in_progress" ? "rgba(212,168,67,0.15)" :
    "transparent"};
  color: ${({ status }) =>
    status === "achieved" ? "var(--nd-success)" :
    status === "in_progress" ? "var(--nd-warning)" :
    "var(--nd-text-disabled)"};
  cursor: ${({ disabled }) => disabled ? "default" : "pointer"};
  outline: none;
  &:disabled { opacity: 0.7; }
  &:hover:not(:disabled) {
    border-color: var(--nd-text-display);
  }
`;

// ── Team Career Cards ──────────────────────────────────────────────

export const TeamCareerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
`;

export const TeamCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-border-visible); }
`;

export const TeamName = styled.h4`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 4px;
`;

export const TeamRole = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
`;

export const TeamPath = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const PathNode = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ current }) => current ? "var(--nd-text-display)" : "var(--nd-border)"};
  background: ${({ current }) => current ? "var(--nd-text-display)" : "transparent"};
  color: ${({ current }) => current ? "var(--nd-black)" : "var(--nd-text-disabled)"};
`;

export const PathArrow = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: var(--nd-text-disabled);
`;

export const TrackSwitch = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px dashed var(--nd-warning);
  color: var(--nd-warning);
  background: rgba(212,168,67,0.1);
`;

export const TeamCerts = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const MiniCert = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--nd-text-disabled);
  background: rgba(255,255,255,0.03);
  padding: 2px 8px;
  border-radius: 4px;
`;

// ── Callout boxes ──────────────────────────────────────────────────

export const GrowthBox = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 48px;
  margin-top: 48px;
  @media (max-width: 768px) { padding: 32px 24px; }
`;

export const GrowthTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 16px;
`;

export const GrowthText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  color: var(--nd-text-secondary);
  line-height: 1.7;
  max-width: 640px;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

export const Honest = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--nd-text-disabled);
  font-style: italic;
`;

// ── Auth bar ───────────────────────────────────────────────────────

export const AuthBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--nd-border);
`;

export const AuthLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

export const AuthButton = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  outline: none;
  transition: border-color 200ms ease;
  &:hover { border-color: var(--nd-text-primary); }
`;

export const SaveStatus = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-success);
  margin-left: 8px;
`;
